import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiSearch, FiChevronLeft,
  FiUser, FiChevronRight, FiChevronDown, FiArrowLeft, FiArrowRight, FiCheckCircle,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../app/routes';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import '../commissionscan/CommissionScan.css';
import '../../styles/QuoteProposal.css';
import {
  getToolboxToken, getNcToken, getOptionStyle,
  INSURANCE_COMPANY_OPTIONS, US_STATES, toDateInput, stripMoney,
  type NowCertsClient, type Policy, type PremFiInsuredData,
} from './premiumFinanceScanShared';
import { submitPremiumFinanceScanToNowCerts } from './premiumFinanceScanSubmit';

/* ── AMS file browser (adapted from AcordSmartScanUpload's inline AmsFileBrowser) ── */
interface AmsFileItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  description?: string;
  size?: string;
  uploader?: string;
  created?: string;
  children?: AmsFileItem[];
}
interface AmsFileSelection { file: AmsFileItem; insuredId: string; folderId: string; }

const AmsFileBrowser: React.FC<{
  insuredId: string;
  clientName: string;
  onFileSelected: (sel: AmsFileSelection | null) => void;
}> = ({ insuredId, clientName, onFileSelected }) => {
  const [rootFolder, setRootFolder] = useState<AmsFileItem | null>(null);
  const [currentFolder, setCurrentFolder] = useState<AmsFileItem | null>(null);
  const [tableData, setTableData] = useState<AmsFileItem[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [selectedFile, setSelectedFile] = useState<AmsFileItem | null>(null);
  const [folderHistory, setFolderHistory] = useState<AmsFileItem[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const mapFiles = (rawFiles: any[]): AmsFileItem[] =>
    (rawFiles || []).map((f: any) => ({
      id: f.databaseId,
      name: f.name,
      type: f.fileOrFolder === 'Folder' ? 'folder' : 'file',
      description: f.fileOrFolder,
      size: f.size?.toString() || '-',
      uploader: f.creatorName || '-',
      created: f.createDate || '-',
      children: f.fileOrFolder === 'Folder' ? [] : undefined,
    }));

  const fetchFiles = useCallback(async (folderId?: string) => {
    const url = `${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/getInsuredFilesList?insuredId=${insuredId}`
      + `&nctoken=${getNcToken()}`
      + `&folderId=${folderId || ''}`;
    const res = await axios.get(url, { headers: { Authorization: `Bearer ${getToolboxToken()}` } });
    return res.data;
  }, [insuredId]);

  useEffect(() => {
    setFetchError('');
    setTableLoading(true);
    fetchFiles()
      .then((data) => {
        const fd = data?.data || data;
        const root: AmsFileItem = {
          id: fd.currentFolderId || insuredId,
          name: fd.name || clientName || 'Root',
          type: 'folder',
          children: mapFiles(fd.files || []),
        };
        setRootFolder(root);
        setCurrentFolder(root);
        setTableData(root.children || []);
        setExpandedFolders({ [root.id]: true });
        setFolderHistory([root]);
        setHistoryIndex(0);
      })
      .catch(() => setFetchError('Unable to load files. Please try again.'))
      .finally(() => setTableLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuredId]);

  const openFolder = useCallback((folder: AmsFileItem, childrenOverride?: AmsFileItem[]) => {
    const children = childrenOverride ?? folder.children ?? [];
    const node: AmsFileItem = { ...folder, children };
    setCurrentFolder(node);
    setTableData(children);
    setExpandedFolders((p) => ({ ...p, [folder.id]: true }));
    setFolderHistory((prev) => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const exists = base.findIndex((f) => f.id === folder.id);
      if (exists !== -1) { setHistoryIndex(exists); return base; }
      const next = [...base, node];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  const updateChildrenInTree = (root: AmsFileItem, targetId: string, newChildren: AmsFileItem[]): AmsFileItem => {
    if (root.id === targetId) return { ...root, children: newChildren };
    if (root.children) return { ...root, children: root.children.map((c) => updateChildrenInTree(c, targetId, newChildren)) };
    return root;
  };

  const toggleFolder = useCallback((item: AmsFileItem) => {
    if (expandedFolders[item.id]) {
      setExpandedFolders((p) => ({ ...p, [item.id]: false }));
      return;
    }
    if (item.children && item.children.length === 0) {
      setTableLoading(true);
      fetchFiles(item.id)
        .then((data) => {
          const newChildren = mapFiles(data?.data?.files || []);
          setRootFolder((prev) => (prev ? updateChildrenInTree(prev, item.id, newChildren) : prev));
          openFolder(item, newChildren);
        })
        .catch(() => {})
        .finally(() => setTableLoading(false));
    } else if (item.type === 'folder') {
      openFolder(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedFolders, fetchFiles, openFolder]);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const handleBack = () => {
    const newIdx = historyIndex - 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders((p) => ({ ...p, [f.id]: true }));
  };
  const handleForward = () => {
    const newIdx = historyIndex + 1;
    const f = folderHistory[newIdx];
    if (!f) return;
    setHistoryIndex(newIdx); setCurrentFolder(f); setTableData(f.children || []);
    setExpandedFolders((p) => ({ ...p, [f.id]: true }));
  };

  const buildPath = (root: AmsFileItem, targetId: string): AmsFileItem[] => {
    const path: AmsFileItem[] = [];
    const dfs = (node: AmsFileItem, acc: AmsFileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === targetId) { path.push(...next); return true; }
      for (const c of node.children || []) if (dfs(c, next)) return true;
      return false;
    };
    dfs(root, []);
    return path.length ? path : [root];
  };

  const fmtSize = (size?: string) => {
    if (!size || size === '-') return '—';
    const n = parseInt(size, 10);
    if (isNaN(n)) return size;
    return n >= 1048576 ? (n / 1048576).toFixed(2) + ' MB' : (n / 1024).toFixed(2) + ' KB';
  };

  const renderTreeItem = (item: AmsFileItem, level = 0): React.ReactNode => {
    const expanded = expandedFolders[item.id];
    return (
      <div key={item.id}>
        <div
          className={`cs-ams-tree-row${level === 0 ? ' cs-ams-tree-row--root' : ''}`}
          style={{ paddingLeft: level * 16 + 10 }}
          onClick={() => toggleFolder(item)}
        >
          {expanded ? <FiChevronDown size={11} className="cs-ams-chevron" /> : <FiChevronRight size={11} className="cs-ams-chevron" />}
          <span className="cs-ams-tree-name" title={item.name}>{item.name}</span>
        </div>
        {expanded && (item.children || []).filter((c) => c.type === 'folder').map((c) => renderTreeItem(c, level + 1))}
      </div>
    );
  };

  if (tableLoading && !rootFolder) {
    return (
      <div className="cs-ams-state" style={{ padding: '32px 0' }}>
        <span className="cs-ams-spinner" />
        <span>Loading files…</span>
      </div>
    );
  }
  if (fetchError) return <div className="cs-ams-error-msg">{fetchError}</div>;

  return (
    <div className="cs-client-panel cs-client-panel--browser">
      <div className="cs-ams-wrap">
        <div className="cs-ams-client-bar">
          <FiUser size={13} className="cs-ams-client-bar__icon" />
          <span className="cs-ams-client-bar__name">{clientName}</span>
        </div>
        {rootFolder && (
          <>
            {selectedFile && (
              <div className="cs-ams-file-card">
                <div className="cs-ams-file-card__left">
                  <FiFileText size={16} className="cs-ams-file-card__icon" />
                  <div>
                    <p className="cs-ams-file-card__name" title={selectedFile.name}>{selectedFile.name}</p>
                    {selectedFile.size && selectedFile.size !== '-' && (
                      <p className="cs-ams-file-card__size">{fmtSize(selectedFile.size)}</p>
                    )}
                  </div>
                </div>
                <button className="cs-ams-file-card__clear" onClick={() => { setSelectedFile(null); onFileSelected(null); }}>
                  ✕
                </button>
              </div>
            )}
            <div className="cs-ams-breadcrumb">
              <button className="cs-ams-nav-btn" disabled={!canGoBack} onClick={handleBack} title="Back"><FiArrowLeft size={13} /></button>
              <button className="cs-ams-nav-btn" disabled={!canGoForward} onClick={handleForward} title="Forward"><FiArrowRight size={13} /></button>
              <div className="cs-ams-breadcrumb__path">
                {buildPath(rootFolder, currentFolder?.id || rootFolder.id).map((seg, i) => (
                  <React.Fragment key={seg.id}>
                    {i > 0 && <span className="cs-ams-breadcrumb__sep">/</span>}
                    <span className="cs-ams-breadcrumb__seg" title={seg.name}>{seg.name}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="cs-ams-layout">
              <div className="cs-ams-tree">{renderTreeItem(rootFolder)}</div>
              <div className="cs-ams-table-wrap">
                {tableLoading ? (
                  <div className="cs-ams-state"><span className="cs-ams-spinner" /><span>Loading…</span></div>
                ) : tableData.length === 0 ? (
                  <div className="cs-ams-state cs-ams-state--empty">No files in this folder.</div>
                ) : (
                  <table className="cs-ams-table">
                    <thead>
                      <tr>
                        <th className="cs-ams-th">Name</th>
                        <th className="cs-ams-th">Type</th>
                        <th className="cs-ams-th">Size</th>
                        <th className="cs-ams-th">Uploader</th>
                        <th className="cs-ams-th">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((item) => {
                        const isSelected = selectedFile?.id === item.id;
                        return (
                          <tr
                            key={item.id}
                            className={`cs-ams-tr${item.type === 'folder' ? ' cs-ams-tr--folder' : ' cs-ams-tr--file'}${isSelected ? ' cs-ams-tr--active' : ''}`}
                            onClick={() => {
                              if (item.type === 'folder') { toggleFolder(item); return; }
                              setSelectedFile(item);
                              onFileSelected({ file: item, insuredId, folderId: currentFolder?.id || '' });
                            }}
                          >
                            <td className="cs-ams-td cs-ams-td--name"><span title={item.name}>{item.name}</span></td>
                            <td className="cs-ams-td">{item.description || '—'}</td>
                            <td className="cs-ams-td">{fmtSize(item.size)}</td>
                            <td className="cs-ams-td">{item.uploader || '—'}</td>
                            <td className="cs-ams-td">{item.created || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Client-search box (Create New / Use Existing client picker) ── */
function useClientSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NowCertsClient[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/customerlist`, {
        params: { searchText: query, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then((r) => setResults(r.data || []))
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [query]);
  return { query, setQuery, results, setResults, loading };
}

/* ── Toggle switch row (Create New / Use Existing) ── */
const ToggleSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string }> = ({ checked, onChange, label }) => (
  <div
    onClick={() => onChange(!checked)}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10, padding: '12px 16px',
      fontSize: 13, fontFamily: "'Poppins', sans-serif", cursor: 'pointer', userSelect: 'none', color: 'var(--text-primary)',
    }}
  >
    {label}
    <span style={{
      position: 'relative', width: 38, height: 20, borderRadius: 999, flexShrink: 0,
      background: checked ? '#7c3aed' : 'var(--border-glass)', transition: 'background 0.15s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: checked ? 20 : 2, width: 16, height: 16,
        borderRadius: '50%', background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }} />
    </span>
  </div>
);

interface Props { onGoToHistory?: () => void; }

const PremiumFinanceScanUpload: React.FC<Props> = ({ onGoToHistory: _onGoToHistory }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const optionStyle = getOptionStyle(theme);
  type Step = 'intake' | 'upload' | 'review';
  const [step, setStep] = useState<Step>('intake');

  /* ── Intake: Client ── */
  const [clientMode, setClientMode] = useState<'createNew' | 'useExisting' | ''>('');
  const clientSearch = useClientSearch();
  const [selectedClient, setSelectedClient] = useState<NowCertsClient | null>(null);
  const [intakeError, setIntakeError] = useState('');

  /* ── Intake: Policy ── */
  const [policyMode, setPolicyMode] = useState<'createNew' | 'useExisting' | ''>('');
  const [existingPolicies, setExistingPolicies] = useState<Policy[]>([]);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);

  /* ── Upload ── */
  const [insuranceCompany, setInsuranceCompany] = useState('');
  const [uploadTab, setUploadTab] = useState<'local' | 'ams'>('local');
  const [file, setFile] = useState<File | null>(null);
  const [selectedAmsFile, setSelectedAmsFile] = useState<AmsFileSelection | null>(null);
  const amsClientSearch = useClientSearch();
  const [amsSelectedClient, setAmsSelectedClient] = useState<NowCertsClient | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ── Review (extracted data) ── */
  const [insuredData, setInsuredData] = useState<PremFiInsuredData | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isUpdateInNowCerts, setIsUpdateInNowCerts] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResults, setSubmitResults] = useState<Array<{ label: string; success: boolean; message: string }>>([]);
  const [ncUrl, setNcUrl] = useState('');
  const [createdNewClient, setCreatedNewClient] = useState(false);

  /* Feedback popup (post-submit) */
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackAnchor, setFeedbackAnchor] = useState<{ bottom: number; right: number } | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<boolean | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);
  const { showSuccess } = useToast();

  /* ── Client selection ── */
  const selectClient = useCallback((client: NowCertsClient) => {
    setSelectedClient(client);
    clientSearch.setQuery('');
    clientSearch.setResults([]);
    setPolicyMode('');
    setSelectedPolicy(null);
    setExistingPolicies([]);
  }, [clientSearch]);

  const getNowCertsPoliciesList = useCallback((insuredId: string) => {
    setPoliciesLoading(true);
    axios.get(`${process.env.REACT_APP_NOWCERTSAPI}/api/nowcert/insuredpolicies`, {
      params: { insuredId, nowCertsToken: getNcToken() },
      headers: { Authorization: `Bearer ${getToolboxToken()}` },
    })
      .then((res) => {
        const list: Policy[] = Array.isArray(res.data) ? res.data : [];
        setExistingPolicies(list.filter((p) => (p.status || '').toLowerCase() === 'active'));
      })
      .catch((err: any) => { if (err.response?.status === 401) navigate('/'); })
      .finally(() => setPoliciesLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (policyMode === 'useExisting' && selectedClient) {
      getNowCertsPoliciesList(selectedClient.databaseId);
    }
  }, [policyMode, selectedClient, getNowCertsPoliciesList]);

  /* ── Intake validation ── */
  const validateIntake = (): boolean => {
    if (clientMode === '') { setIntakeError('Please select a client action'); return false; }
    if (clientMode === 'useExisting' && !selectedClient) { setIntakeError('Please select a client'); return false; }
    if (policyMode === '') { setIntakeError('Please select a policy action'); return false; }
    if (policyMode === 'useExisting' && !selectedPolicy) { setIntakeError('Please select an existing policy'); return false; }
    setIntakeError('');
    return true;
  };

  const onNextIntake = () => { if (validateIntake()) setStep('upload'); };
  const onBackToIntake = () => setStep('intake');

  /* ── File handling ── */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find((f) => /\.pdf$/i.test(f.name));
    if (first) { setFile(first); setUploadError(''); }
    else { setUploadError('Please select PDF files only.'); setTimeout(() => setUploadError(''), 3000); }
  }, []);
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); pickFile(e.dataTransfer.files); };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files) pickFile(e.target.files); e.target.value = ''; };

  const canScan = !uploadLoading && !!insuranceCompany &&
    (uploadTab === 'local' ? !!file : !!selectedAmsFile);

  const amsBrowseClient = clientMode === 'useExisting' ? selectedClient : amsSelectedClient;

  /* ── Build selectionOptionJson + start scan ── */
  const onStartScan = useCallback(async () => {
    if (!insuranceCompany) { setUploadError('Please select insurance Company type.'); return; }
    const isAms = uploadTab === 'ams';
    if (!isAms && !file) return;
    if (isAms && !selectedAmsFile) return;

    setUploadLoading(true);
    setUploadError('');
    try {
      const folderId = isAms ? (selectedAmsFile?.folderId || '') : '';
      const fileIds = isAms ? [selectedAmsFile?.file.id || ''] : [];
      const insuredIdForRequest = clientMode === 'useExisting'
        ? (selectedClient?.databaseId || '')
        : (amsSelectedClient?.databaseId || '');

      const selectionOptionJson = JSON.stringify({
        ClientName: selectedClient?.name || amsSelectedClient?.name || '',
        InsuredId: insuredIdForRequest,
        folderId, fileIds,
        isFileUploadFromAMSParam: isAms,
        createNewPolicy: policyMode === 'createNew',
        createNewInsured: clientMode === 'createNew',
      });

      const data = new FormData();
      if (!isAms && file) data.append('file', file);
      data.append('insuranceCompanyName', insuranceCompany);
      fileIds.forEach((id) => data.append('amsFileIds', id));
      data.append('folderId', folderId);
      data.append('insuredId', insuredIdForRequest);
      data.append('isFileUploadFromAMS', String(isAms));
      data.append('ncToken', getNcToken());
      data.append('selectionOptionJson', selectionOptionJson);

      const res = await axios.post(`${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscan`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      const grossPremiumList: any[] = Array.isArray(res.data.grossPremium) ? res.data.grossPremium : [];
      const first = grossPremiumList[0] || {};
      const extracted: PremFiInsuredData = {
        insuredFirstName: res.data.insuredName || '',
        policyNumber: policyMode !== 'createNew' && selectedPolicy ? selectedPolicy.policyNumber : (first.policyNumber || ''),
        premium: stripMoney(first.premium),
        lineOfBusiness: first.lineOfBusiness || '',
        term: first.term || '',
        carrierName: first.carrierName || '',
        effectiveDate: first.effectiveDate || '',
        quoteNumber: (res.data.quoteNumber || '').split('.')[0],
        downPayment: stripMoney(first.downPayment),
        financedAmount: stripMoney(first.amountFinanced),
        effectiveAPR: res.data.effectiveAPR || '',
        otherTaxes: first.taxes != null ? stripMoney(first.taxes) : '0',
        otherFees: first.fees != null ? stripMoney(first.fees) : '0',
        addressLine1: res.data.addressLine1 || '',
        city: (res.data.city || '').replace(/,/g, ''),
        state: res.data.state || '',
        zipCode: res.data.zipCode || '',
        submissionId: res.data.submissionId || '',
        fileUrl: res.data.fileurl || '',
      };
      setInsuredData(extracted);
      setFieldErrors({});
      setStep('review');
    } catch (err: any) {
      if (err?.response?.status === 401) {
        navigate(ROUTES.LOGIN);
      } else {
        setUploadError(err.response?.data?.errorMessage || 'Error while creating the request.');
      }
    } finally {
      setUploadLoading(false);
    }
  }, [insuranceCompany, uploadTab, file, selectedAmsFile, clientMode, selectedClient, amsSelectedClient, policyMode, selectedPolicy, navigate]);

  const resetAll = useCallback(() => {
    setStep('intake');
    setClientMode(''); setSelectedClient(null); setIntakeError('');
    setPolicyMode(''); setExistingPolicies([]); setSelectedPolicy(null);
    setInsuranceCompany(''); setUploadTab('local'); setFile(null); setSelectedAmsFile(null);
    setAmsSelectedClient(null); amsClientSearch.setQuery(''); amsClientSearch.setResults([]);
    setUploadError('');
    setInsuredData(null); setFieldErrors({});
    setIsUpdateInNowCerts(false); setSubmitResults([]);
    setNcUrl(''); setCreatedNewClient(false);
    setFeedbackOpen(false); setFeedbackAnchor(null); setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, [amsClientSearch]);

  /* ── Feedback ── */
  const openFeedback = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
    setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right: window.innerWidth - rect.right });
    setFeedbackOpen(true);
  }, []);
  const closeFeedback = useCallback(() => {
    setFeedbackOpen(false); setFeedbackAnchor(null); setFeedbackText(''); setFeedbackRating(null); setFeedbackError('');
  }, []);
  const sendFeedback = useCallback((isLiked: boolean | null, text: string) => {
    const submissionId = insuredData?.submissionId || '';
    if (!submissionId) return;
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscan/isliked`,
      { TransactionId: submissionId, IsLiked: isLiked, Feedback: text ?? '' },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully!')).catch(() => {});
  }, [insuredData, showSuccess]);
  const onThumbsUpClick = useCallback(() => {
    const next = feedbackRating === true ? null : true;
    sendFeedback(next, feedbackText); closeFeedback();
  }, [feedbackRating, feedbackText, sendFeedback, closeFeedback]);
  const onThumbsDownClick = useCallback(() => {
    const next = feedbackRating === false ? null : false;
    sendFeedback(next, feedbackText); closeFeedback();
  }, [feedbackRating, feedbackText, sendFeedback, closeFeedback]);
  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackText.trim()) { setFeedbackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedbackError('Please provide your feedback within 250 characters'); return; }
    setFeedbackError('');
    setFeedbackSubmitting(true);
    axios.post(
      `${process.env.REACT_APP_DataFabric_Url}/api/OCR/pfscan/feedback`,
      { TransactionId: insuredData?.submissionId || '', Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToolboxToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => setFeedbackSubmitting(false));
  }, [feedbackText, insuredData, closeFeedback, showSuccess]);

  useEffect(() => {
    if (!feedbackOpen) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackOpen, closeFeedback]);

  /* ── Field updates ── */
  const updateInsured = (patch: Partial<PremFiInsuredData>) => setInsuredData((p) => (p ? { ...p, ...patch } : p));

  /* ── Submit ── */
  const validateInsuredForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!insuredData) return false;
    if (!insuredData.insuredFirstName.trim()) errors.insuredFirstName = 'Insured name is required';
    if (!insuredData.policyNumber.trim()) errors.policyNumber = 'Policy number is required';
    if (!insuredData.premium.trim()) errors.premium = 'Premium amount is required';
    if (!insuredData.effectiveDate.trim()) errors.effectiveDate = 'Effective date is required';
    if (!insuredData.term.trim()) errors.term = 'Term is required';
    if (!insuredData.zipCode.trim()) errors.zipCode = 'Zip code is required';
    if (!insuredData.state.trim()) errors.state = 'State is required';
    if (!insuredData.lineOfBusiness.trim()) errors.lineOfBusiness = 'Coverage code is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmitClick = () => { if (validateInsuredForm()) setShowSubmitConfirm(true); };

  const onSubmitConfirmed = useCallback(async () => {
    if (!insuredData) return;
    setShowSubmitConfirm(false);
    setSubmitLoading(true);
    setSubmitResults([]);
    try {
      const createNewClient = clientMode === 'createNew';
      const createNewPolicyFlag = policyMode === 'createNew';
      const insuredId = clientMode === 'useExisting' ? (selectedClient?.databaseId || '') : '';
      const existingPolicyId = policyMode === 'useExisting' ? (selectedPolicy?.databaseId || '') : '';

      const { ncUrl: newNcUrl, createdNewClient: newClientCreated } = await submitPremiumFinanceScanToNowCerts({
        insured: insuredData,
        createNewClient,
        createNewPolicyFlag,
        insuredId,
        existingPolicyId,
      }, (s) => setSubmitResults((prev) => [...prev, s]));

      setNcUrl(newNcUrl);
      setCreatedNewClient(newClientCreated);
      setIsUpdateInNowCerts(true);
    } finally {
      setSubmitLoading(false);
    }
  }, [insuredData, clientMode, policyMode, selectedClient, selectedPolicy]);

  /* ════════════════════ RENDER: STEP 1 — INTAKE ════════════════════ */
  if (step === 'intake') {
    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)' }}>Client</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ToggleSwitch
                checked={clientMode === 'createNew'}
                onChange={(v) => { setClientMode(v ? 'createNew' : ''); setSelectedClient(null); setPolicyMode(''); setSelectedPolicy(null); setExistingPolicies([]); }}
                label="Create New"
              />
              <ToggleSwitch
                checked={clientMode === 'useExisting'}
                onChange={(v) => { setClientMode(v ? 'useExisting' : ''); if (!v) { setSelectedClient(null); setPolicyMode(''); setSelectedPolicy(null); setExistingPolicies([]); } }}
                label="Use Existing"
              />
            </div>
            {clientMode === 'useExisting' && (
              <div className="cs-client-search" style={{ marginTop: 10 }}>
                <div className="cs-client-search__input-wrap">
                  <FiSearch size={15} className="cs-client-search__icon" />
                  <input
                    className="cs-client-search__input"
                    placeholder="Search client name…"
                    value={selectedClient ? selectedClient.name : clientSearch.query}
                    onChange={(e) => { setSelectedClient(null); clientSearch.setQuery(e.target.value); }}
                  />
                  {clientSearch.loading && <span className="cs-client-search__spinner" />}
                </div>
                {clientSearch.results.length > 0 && !selectedClient && (
                  <ul className="cs-client-dropdown">
                    {clientSearch.results.map((c) => (
                      <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectClient(c)}>
                        <span className="cs-client-dropdown__name">{c.name}</span>
                        {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)' }}>Policy</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <ToggleSwitch checked={policyMode === 'createNew'} onChange={(v) => { setPolicyMode(v ? 'createNew' : ''); setSelectedPolicy(null); }} label="Create New" />
              <ToggleSwitch
                checked={policyMode === 'useExisting'}
                onChange={(v) => {
                  if (v && !(clientMode === 'useExisting' && selectedClient)) { setIntakeError('Please select an existing client first'); return; }
                  setPolicyMode(v ? 'useExisting' : ''); setSelectedPolicy(null);
                }}
                label="Use Existing"
              />
            </div>

            {policyMode === 'useExisting' && (
              policiesLoading ? (
                <div className="cs-ams-state" style={{ marginTop: 10 }}><span className="cs-ams-spinner" /><span>Loading policies…</span></div>
              ) : existingPolicies.length === 0 ? (
                <div className="cs-ams-state cs-ams-state--empty" style={{ marginTop: 10 }}>No existing policies found for this client.</div>
              ) : (
                <div className="qp-table-wrap glass-surface" style={{ marginTop: 10 }}>
                  <table className="qp-table">
                    <thead>
                      <tr>
                        <th className="qp-table__th" style={{ textAlign: 'center' }}>Select</th>
                        <th className="qp-table__th">Policy Number</th>
                        <th className="qp-table__th">Line of Business</th>
                        <th className="qp-table__th">Effective Date</th>
                        <th className="qp-table__th">Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existingPolicies.map((p) => {
                        const isSelected = selectedPolicy?.databaseId === p.databaseId;
                        return (
                          <tr
                            key={p.databaseId}
                            className={`qp-table__row${isSelected ? ' cs-ams-tr--active' : ''}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedPolicy(p)}
                          >
                            <td className="qp-table__td" style={{ textAlign: 'center' }}>
                              <input
                                type="radio"
                                name="existingPolicy"
                                checked={isSelected}
                                onChange={() => setSelectedPolicy(p)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="qp-table__td">{p.policyNumber}</td>
                            <td className="qp-table__td">{(p.linesOfBusiness || [])[0] || '—'}</td>
                            <td className="qp-table__td">{p.effectiveDate ? new Date(p.effectiveDate).toLocaleDateString() : '—'}</td>
                            <td className="qp-table__td">{p.totalPremium != null ? `$${p.totalPremium}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>

        {intakeError && <div style={{ color: '#dc2626', fontSize: 12 }}>{intakeError}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onNextIntake}>
            Next →
          </motion.button>
        </div>
      </div>
    );
  }

  /* ════════════════════ RENDER: STEP 2 — UPLOAD ════════════════════ */
  if (step === 'upload') {
    const intakeSummary: Array<{ label: string; value: string }> = [
      { label: 'Client', value: clientMode === 'useExisting' ? (selectedClient?.name || '—') : 'Create New Client' },
      { label: 'Policy', value: policyMode === 'useExisting' ? (selectedPolicy?.policyNumber || '—') : 'Create New Policy' },
    ];

    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <button className="cs-btn-cancel" onClick={onBackToIntake} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiChevronLeft size={13} /> Back
          </button>
          {intakeSummary.map((it, i) => (
            <span key={i} style={{ fontSize: 13, fontFamily: "'Poppins', sans-serif" }}>
              <strong style={{ color: 'var(--text-primary)' }}>{it.label}:</strong>{' '}
              <span style={{ color: 'var(--text-secondary)' }}>{it.value}</span>
            </span>
          ))}
        </div>

        <div className="cs-tabs">
          <button className={`cs-tab${uploadTab === 'local' ? ' cs-tab--active' : ''}`} onClick={() => { setUploadTab('local'); setSelectedAmsFile(null); }}>
            Select Document from Local Drive
          </button>
          <button className={`cs-tab${uploadTab === 'ams' ? ' cs-tab--active' : ''}`} onClick={() => { setUploadTab('ams'); setFile(null); }}>
            Select Document from Client Profile
          </button>
        </div>

        <div className="cs-content">
          {uploadTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              <div className="ho-form-field" style={{ maxWidth: 260, width: '100%', marginBottom: 20 }}>
                <select className="ho-select" value={insuranceCompany} onChange={(e) => setInsuranceCompany(e.target.value)}>
                  <option value="" style={optionStyle}>Select Insurance Company</option>
                  {INSURANCE_COMPANY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>
                  ))}
                </select>
              </div>

              {!file ? (
                <>
                  <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                  <p className="cs-dropzone__title">Drag & Drop your document here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button className="cs-browse-btn" onClick={() => fileInputRef.current?.click()} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">
                    Supported document types: Capital Premium Financing and IPFS. Allowed file format: PDF (single file only).
                  </p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={handleFileInput} />
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                <div className="cs-client-search" style={{ flex: 1 }}>
                  <div className="cs-client-search__input-wrap">
                    <FiSearch size={15} className="cs-client-search__icon" />
                    <input
                      className="cs-client-search__input"
                      placeholder="Enter client name to browse their AMS files"
                      readOnly={clientMode === 'useExisting'}
                      value={clientMode === 'useExisting' ? (selectedClient?.name || '') : (amsSelectedClient ? amsSelectedClient.name : amsClientSearch.query)}
                      onChange={(e) => {
                        if (clientMode === 'useExisting') return;
                        setAmsSelectedClient(null);
                        setSelectedAmsFile(null);
                        amsClientSearch.setQuery(e.target.value);
                      }}
                    />
                    {amsClientSearch.loading && <span className="cs-client-search__spinner" />}
                  </div>
                  {amsClientSearch.results.length > 0 && !amsSelectedClient && clientMode !== 'useExisting' && (
                    <ul className="cs-client-dropdown">
                      {amsClientSearch.results.map((c) => (
                        <li
                          key={c.databaseId}
                          className="cs-client-dropdown__item"
                          onClick={() => { setAmsSelectedClient(c); amsClientSearch.setQuery(''); amsClientSearch.setResults([]); setSelectedAmsFile(null); }}
                        >
                          <span className="cs-client-dropdown__name">{c.name}</span>
                          {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="ho-form-field" style={{ flex: 1, margin: 0 }}>
                  <select className="ho-select" value={insuranceCompany} onChange={(e) => setInsuranceCompany(e.target.value)}>
                    <option value="" style={optionStyle}>Select Insurance Company</option>
                    {INSURANCE_COMPANY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} style={optionStyle}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {amsBrowseClient && (
                <AmsFileBrowser
                  insuredId={amsBrowseClient.databaseId}
                  clientName={amsBrowseClient.name}
                  onFileSelected={setSelectedAmsFile}
                />
              )}
            </>
          )}
        </div>

        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>This tool works best with readable, text-based PDFs — scanned/image PDFs may not process correctly.</span>
        </div>

        {uploadError && <div style={{ color: '#dc2626', fontSize: 12, padding: '4px 0' }}>{uploadError}</div>}

        <div className="cs-footer">
          <span className="cs-footer__status">
            {uploadTab === 'local'
              ? (!file ? 'No file added yet.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)}
          </span>
          <div className="cs-footer__actions">
            <button className="cs-btn-cancel" onClick={resetAll} disabled={uploadLoading}>Cancel</button>
            <motion.button className="cs-btn-start" disabled={!canScan} whileHover={{ scale: canScan ? 1.03 : 1 }} whileTap={{ scale: canScan ? 0.97 : 1 }} onClick={onStartScan}>
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════ RENDER: STEP 3 — REVIEW ════════════════════ */
  if (!insuredData) return null;
  const isReadOnly = isUpdateInNowCerts;

  const ctrlStyle = (editable: boolean): React.CSSProperties => ({
    width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 12,
    background: editable ? 'var(--surface-glass)' : 'rgba(255,255,255,0.03)',
    border: `1px solid ${editable ? 'var(--border-glass)' : 'rgba(255,255,255,0.06)'}`,
    color: editable ? 'var(--text-primary)' : 'var(--text-secondary)',
    outline: 'none', boxSizing: 'border-box' as const, cursor: editable ? 'text' : 'default', minWidth: 0,
  });

  const fieldWrap = (label: string, node: React.ReactNode, error?: string, required?: boolean) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </div>
      {node}
      {error && <span style={{ fontSize: 11, color: '#ef4444', marginTop: 3, display: 'block' }}>{error}</span>}
    </div>
  );

  const reviewIntakeSummary: Array<{ label: string; value: string }> = [
    { label: 'Client', value: clientMode === 'useExisting' ? (selectedClient?.name || '—') : 'Create New Client' },
    { label: 'Policy', value: policyMode === 'useExisting' ? (selectedPolicy?.policyNumber || '—') : 'Create New Policy' },
  ];

  return (
    <>
      <div className="cs-main" style={{ padding: 0, gap: 14, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <button className="cs-btn-cancel" onClick={() => setStep('upload')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiChevronLeft size={13} /> Back
            </button>
            {reviewIntakeSummary.map((it, i) => (
              <span key={i} style={{ fontSize: 13, fontFamily: "'Poppins', sans-serif" }}>
                <strong style={{ color: 'var(--text-primary)' }}>{it.label}:</strong>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>{it.value}</span>
              </span>
            ))}
          </div>
          {isUpdateInNowCerts && (
            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981' }}>
              ✓ Updated in AMS
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
          {/* Left: scanned document */}
          <div className="cspr-pdf-panel" style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-glass)', height: '100%' }}>
            <div className="cspr-pdf-panel__header">
              <span className="cspr-pdf-panel__title">Scanned Document</span>
            </div>
            <div className="cspr-pdf-viewer">
              {insuredData.fileUrl ? (
                <iframe src={`${insuredData.fileUrl}#zoom=90`} title="Scanned Document" style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : (
                <div className="cspr-pdf-placeholder">Preview not available</div>
              )}
            </div>
          </div>

          {/* Right: extracted data */}
          <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ display: 'flex' }}>
              <span style={{
                fontSize: 12, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)',
                background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderBottom: 'none',
                borderRadius: '10px 10px 0 0', padding: '9px 18px',
              }}>
                Insured Details
              </span>
            </div>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: 14, padding: 16,
              border: '1px solid var(--border-glass)', borderRadius: '0 10px 10px 10px',
              background: 'rgba(255,255,255,0.03)',
            }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)', margin: 0, borderBottom: '1px solid var(--border-glass)', paddingBottom: 8 }}>
                Policy Information
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '14px 14px' }}>
                {fieldWrap('Insured Name', (
                  <input value={insuredData.insuredFirstName} readOnly={isReadOnly} onChange={(e) => updateInsured({ insuredFirstName: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.insuredFirstName, true)}
                {fieldWrap('Policy Number', (
                  <input value={insuredData.policyNumber} readOnly={isReadOnly} onChange={(e) => updateInsured({ policyNumber: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.policyNumber, true)}
                {fieldWrap('Premium Amount ($)', (
                  <input value={insuredData.premium} readOnly={isReadOnly} onChange={(e) => updateInsured({ premium: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.premium, true)}
                {fieldWrap('Coverage Code', (
                  <input value={insuredData.lineOfBusiness} readOnly={isReadOnly} onChange={(e) => updateInsured({ lineOfBusiness: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.lineOfBusiness, true)}
                {fieldWrap('Term (months)', (
                  <input value={insuredData.term} readOnly={isReadOnly} onChange={(e) => updateInsured({ term: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.term, true)}
                {fieldWrap('Carrier Name', (
                  <input value={insuredData.carrierName} readOnly={isReadOnly} onChange={(e) => updateInsured({ carrierName: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Effective Date', (
                  <input type="date" value={toDateInput(insuredData.effectiveDate)} readOnly={isReadOnly} onChange={(e) => updateInsured({ effectiveDate: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ), fieldErrors.effectiveDate, true)}
                {fieldWrap('Quote Number', (
                  <input value={insuredData.quoteNumber} readOnly={isReadOnly} onChange={(e) => updateInsured({ quoteNumber: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Down Payment ($)', (
                  <input value={insuredData.downPayment} readOnly={isReadOnly} onChange={(e) => updateInsured({ downPayment: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Financed Amount ($)', (
                  <input value={insuredData.financedAmount} readOnly={isReadOnly} onChange={(e) => updateInsured({ financedAmount: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Effective APR', (
                  <input value={insuredData.effectiveAPR} readOnly={isReadOnly} onChange={(e) => updateInsured({ effectiveAPR: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Taxes ($)', (
                  <input value={insuredData.otherTaxes} readOnly={isReadOnly} onChange={(e) => updateInsured({ otherTaxes: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
                {fieldWrap('Fees ($)', (
                  <input value={insuredData.otherFees} readOnly={isReadOnly} onChange={(e) => updateInsured({ otherFees: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                ))}
              </div>

              <div style={{
                display: 'flex', flexDirection: 'column', gap: 14, marginTop: 4,
              }}>
                <h4 style={{ fontSize: 13, fontWeight: 700, fontFamily: "'Poppins', sans-serif", color: 'var(--text-primary)', margin: 0 }}>
                  Address Information
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '14px 14px' }}>
                  {fieldWrap('Address Line 1', (
                    <input value={insuredData.addressLine1} readOnly={isReadOnly} onChange={(e) => updateInsured({ addressLine1: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                  ))}
                  {fieldWrap('City', (
                    <input value={insuredData.city} readOnly={isReadOnly} onChange={(e) => updateInsured({ city: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                  ))}
                  {fieldWrap('State', (
                    <select value={insuredData.state} disabled={isReadOnly} onChange={(e) => updateInsured({ state: e.target.value })} style={ctrlStyle(!isReadOnly)}>
                      <option value="" style={optionStyle}>Select State</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code} style={optionStyle}>{s.name}</option>
                      ))}
                    </select>
                  ), fieldErrors.state, true)}
                  {fieldWrap('Zip Code', (
                    <input value={insuredData.zipCode} readOnly={isReadOnly} onChange={(e) => updateInsured({ zipCode: e.target.value })} style={ctrlStyle(!isReadOnly)} />
                  ), fieldErrors.zipCode, true)}
                </div>
              </div>
            </div>

            {submitResults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--surface-glass)', border: '1px solid var(--border-glass)', borderRadius: 10, padding: 12, marginTop: 14 }}>
                {submitResults.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: r.success ? '#10b981' : '#ef4444' }}>
                    {r.success ? <FiCheckCircle size={13} /> : <FiAlertCircle size={13} />}
                    <strong>{r.label}:</strong> {r.message}
                  </div>
                ))}
              </div>
            )}

            {!isReadOnly && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} disabled={submitLoading} onClick={onSubmitClick}>
                  {submitLoading ? 'Submitting…' : 'Submit'}
                </motion.button>
              </div>
            )}

            {isReadOnly && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
                {ncUrl && createdNewClient && (
                  <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => window.open(ncUrl, '_blank')}>
                    Open Insured
                  </motion.button>
                )}
                <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={resetAll}>
                  Upload Again
                </motion.button>
                <motion.button className="cs-btn-start" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={openFeedback}>
                  Feedback
                </motion.button>
              </div>
            )}
          </div>
        </div>

        {feedbackOpen && feedbackAnchor && (
          <div ref={feedbackRef} className="csfb-popup" style={{ bottom: feedbackAnchor.bottom, right: feedbackAnchor.right }}>
            <h4 className="csfb-title">Feedback</h4>
            <textarea className="csfb-textarea" placeholder="Enter your feedback here" value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={4} />
            <div className="csfb-rating">
              <button className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`} onClick={onThumbsUpClick} title="Thumbs up"><FiThumbsUp size={20} /></button>
              <button className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`} onClick={onThumbsDownClick} title="Thumbs down"><FiThumbsDown size={20} /></button>
            </div>
            {feedbackError && <p className="csfb-error">{feedbackError}</p>}
            <div className="csfb-actions">
              <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
              <button className="csfb-btn-submit" disabled={feedbackSubmitting} onClick={onSubmitFeedbackClick}>
                {feedbackSubmitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        )}

        {showSubmitConfirm && ReactDOM.createPortal(
          <div className="csams-overlay" onClick={() => setShowSubmitConfirm(false)}>
            <div className="csams-modal" onClick={(e) => e.stopPropagation()}>
              <div className="csams-modal__header">
                <h3 className="csams-modal__title">Confirm Submit</h3>
                <button className="csams-modal__close" onClick={() => setShowSubmitConfirm(false)}>✕</button>
              </div>
              <div className="csams-modal__body">
                <p style={{ fontSize: 13, color: '#111827', fontFamily: "'Poppins', sans-serif", margin: 0 }}>
                  By clicking <span style={{ color: '#ef4444', fontWeight: 700 }}>Submit</span>, this transaction will be recorded in NowCerts
                  {clientMode === 'createNew' ? ' and a new client/policy will be created' : policyMode === 'createNew' ? ' and a new policy will be created' : ''}.
                  Are you sure you want to proceed?
                </p>
              </div>
              <div className="csams-modal__footer">
                <button className="csams-btn csams-btn--cancel" onClick={() => setShowSubmitConfirm(false)}>Cancel</button>
                <button className="csams-btn csams-btn--upload" onClick={onSubmitConfirmed}>Confirm</button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </>
  );
};

export default PremiumFinanceScanUpload;
